import { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  BookOpen,
  CheckCircle,
  TrendingUp,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Target,
  Award,
  BarChart3,
  Lightbulb,
  ArrowLeft,
  RefreshCw,
  Trophy,
  Flame,
  Star,
  Zap,
  FileText,
} from "lucide-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { format, formatDistanceToNow } from "date-fns";
import {
  MoatCards,
  TopicMasteryBadge,
  TopicNationalStanding,
  type AbilityEstimate,
  type ReviewDueTopic,
} from "@/components/report/MoatCards";
import { StudentEmptyState } from "@/components/StudentEmptyState";
import { Illustration } from "@/components/Illustration";
import { subjectStyleFor } from "@/lib/subjectStyles";

/** Flat section: solid icon sticker, title, optional hint, content. */
function FlatSection({ icon: Icon, sticker, title, hint, children, tone = "", right, testId }: { icon: any; sticker: string; title: string; hint?: string; children: React.ReactNode; tone?: string; right?: React.ReactNode; testId?: string }) {
  return (
    <section className={`rounded-xl border p-4 md:p-5 space-y-3 ${tone || "border-border bg-card"}`} data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${sticker} text-white flex items-center justify-center shrink-0 -rotate-2 shadow-sm`}><Icon className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1"><h2 className="text-base md:text-lg font-semibold leading-tight">{title}</h2>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>
        {right}
      </div>
      {children}
    </section>
  );
}
function scoreTone(score: number) { return score >= 80 ? "bg-emerald-500" : score >= 60 ? "bg-amber-500" : "bg-rose-500"; }
function ScorePill({ score }: { score: number }) { return <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white tabular-nums ${scoreTone(score)}`}>{score}%</span>; }
function Bar({ value, color, track = "bg-muted", h = "h-2" }: { value: number; color: string; track?: string; h?: string }) {
  return <div className={`${h} rounded-full ${track} overflow-hidden`} role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}><div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} /></div>;
}

interface StrandData {
  name: string;
  score_percent: number;
  status: string;
}

interface SubjectReport {
  name: string;
  summary: string;
  total_questions: number;
  correct: number;
  score_percent: number;
  strands: StrandData[];
  can_do: string[];
  working_on: string[];
  needs_help: string[];
  parent_tips: string[];
}

interface ReportData {
  student: {
    id: string;
    name: string;
    grade_level: string;
  };
  report_date: string;
  subjects: SubjectReport[];
}

interface ObjectiveData {
  objectiveCode: string;
  objectiveDescription: string;
  standardName: string | null;
  topicName: string | null;
  periodName: string | null;
  periodShortCode: string | null;
  subject: string;
  totalAttempts: number;
  correctAttempts: number;
  accuracy: number;
  mastery: "mastered" | "progressing" | "not-started";
  lastAttemptedAt: string | null;
  masteredAt: string | null;
}

interface PeriodGroup {
  periodName: string;
  objectives: ObjectiveData[];
  masteredCount: number;
  total: number;
}

interface TopicGroup {
  topicName: string;
  periods: PeriodGroup[];
  masteredCount: number;
  total: number;
}

interface WinData {
  objectiveCode: string;
  objectiveDescription: string;
  topicName: string | null;
  masteredAt: string;
}

interface FocusData {
  objectiveCode: string;
  objectiveDescription: string;
  topicName: string | null;
  subject: string;
  attemptTopic: string;
  accuracy: number;
  totalAttempts: number;
  correctAttempts: number;
  attemptsNeeded: number;
}

interface ObjectivesReport {
  totalObjectives: number;
  masteredCount: number;
  progressingCount: number;
  notStartedCount: number;
  subjectBreakdown: TopicGroup[];
  recentWins: WinData[];
  focusAreas: FocusData[];
  overallStats: {
    totalAttempts: number;
    correctAttempts: number;
    overallAccuracy: number;
    streak: number;
  };
}

interface ReadingStats {
  totalMaterials: number;
  completed: number;
  inProgress: number;
  totalTimeSeconds: number;
  completedMaterials: Array<{
    id: string;
    title: string;
    subject: string;
    topic: string;
    completedAt: string;
  }>;
}

const SUBJECT_SORT_ORDER: Record<string, number> = {
  "mathematics": 1, "math": 1,
  "language arts": 2, "english": 2,
  "science": 3,
  "social studies": 4,
  "ability test": 5,
};

function getSubjectSortOrder(name: string): number {
  return SUBJECT_SORT_ORDER[name.toLowerCase()] ?? 99;
}

function deduplicateSubjects(subjects: SubjectReport[]): SubjectReport[] {
  const map = new Map<string, SubjectReport>();
  for (const s of subjects) {
    const key = s.name.toLowerCase().trim();
    const existing = map.get(key);
    if (!existing) {
      const SUBJECT_NAMES: Record<string, string> = { "mathematics": "Mathematics", "math": "Mathematics", "science": "Science", "social studies": "Social Studies", "language arts": "Language Arts", "english": "Language Arts", "ability test": "Ability Test" };
      const displayName = SUBJECT_NAMES[key] || s.name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      map.set(key, { ...s, name: displayName, strands: [...s.strands], can_do: [...s.can_do], working_on: [...s.working_on], needs_help: [...s.needs_help], parent_tips: [...s.parent_tips] });
    } else {
      const totalQ = existing.total_questions + s.total_questions;
      const totalC = existing.correct + s.correct;
      existing.total_questions = totalQ;
      existing.correct = totalC;
      existing.score_percent = totalQ > 0 ? Math.round((totalC / totalQ) * 100) : 0;
      if (s.summary && s.summary !== existing.summary) {
        existing.summary = existing.summary ? `${existing.summary} ${s.summary}` : s.summary;
      }
      const strandNames = new Set(existing.strands.map(st => st.name));
      for (const strand of s.strands) {
        if (!strandNames.has(strand.name)) {
          existing.strands.push(strand);
          strandNames.add(strand.name);
        }
      }
      for (const item of s.can_do) if (!existing.can_do.includes(item)) existing.can_do.push(item);
      for (const item of s.working_on) if (!existing.working_on.includes(item)) existing.working_on.push(item);
      for (const item of s.needs_help) if (!existing.needs_help.includes(item)) existing.needs_help.push(item);
      for (const item of s.parent_tips) if (!existing.parent_tips.includes(item)) existing.parent_tips.push(item);
    }
  }
  return Array.from(map.values()).sort((a, b) => getSubjectSortOrder(a.name) - getSubjectSortOrder(b.name));
}

function scoreColor(score: number): string {
  if (score >= 80) return "text-green-600 dark:text-green-400";
  if (score >= 60) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

function scoreBadgeVariant(score: number): "default" | "secondary" | "destructive" {
  if (score >= 80) return "default";
  if (score >= 60) return "secondary";
  return "destructive";
}

function scoreLabel(score: number): string {
  if (score >= 90) return "Excellent";
  if (score >= 80) return "Very Good";
  if (score >= 70) return "Good";
  if (score >= 60) return "Satisfactory";
  if (score >= 50) return "Needs Improvement";
  return "Needs Attention";
}

function progressColor(score: number): string {
  if (score >= 80) return "[&>div]:bg-green-500";
  if (score >= 60) return "[&>div]:bg-amber-500";
  return "[&>div]:bg-red-500";
}

function SubjectCard({ subject }: { subject: SubjectReport }) {
  const [expanded, setExpanded] = useState(false);
  const st = subjectStyleFor(subject.name);
  const status = (v: string) => v === "mastered" ? { label: "Strong", cls: "bg-emerald-500/15 text-emerald-800 dark:text-emerald-200" } : v === "progressing" ? { label: "Developing", cls: "bg-amber-500/15 text-amber-800 dark:text-amber-200" } : { label: "Needs practice", cls: "bg-rose-500/15 text-rose-800 dark:text-rose-200" };
  return (
    <div className={`rounded-xl border ${st.card}`} data-testid={`subject-card-${subject.name}`}>
      <button type="button" className="w-full text-left p-4" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${st.sticker} flex items-center justify-center shrink-0 -rotate-2 shadow-sm`}>{expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold truncate">{subject.name}</h3>
              <div className="flex items-center gap-2 shrink-0">
                <span className="hidden sm:inline text-xs font-semibold px-2 py-0.5 rounded-full bg-white/60 dark:bg-black/20">{scoreLabel(subject.score_percent)}</span>
                <span className={`text-xl font-bold tabular-nums ${scoreColor(subject.score_percent)}`}>{subject.score_percent}%</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground tabular-nums">{subject.correct} of {subject.total_questions} correct</p>
            <div className="mt-2"><Bar value={subject.score_percent} color={st.bar} track={st.track} /></div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-3 line-clamp-2 sm:line-clamp-none">{subject.summary}</p>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {subject.strands.length > 0 && (
            <div className="rounded-lg bg-card border border-border p-3 space-y-3">
              <h4 className="text-sm font-semibold flex items-center gap-2"><Target className="h-4 w-4 text-primary" />Learning areas</h4>
              {subject.strands.map((strand) => {
                const stt = status(strand.status);
                return (
                  <div key={strand.name} className="space-y-1">
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="min-w-0 flex-1 leading-snug">{strand.name}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${stt.cls}`}>{stt.label}</span>
                        <span className={`font-semibold tabular-nums ${scoreColor(strand.score_percent)}`}>{strand.score_percent}%</span>
                      </div>
                    </div>
                    <Bar value={strand.score_percent} color={scoreTone(strand.score_percent)} h="h-1.5" />
                  </div>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 gap-3">
            {subject.can_do.length > 0 && (
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 space-y-1.5">
                <h4 className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-1.5"><CheckCircle className="h-4 w-4" />Strengths</h4>
                <ul className="space-y-1">{subject.can_do.map((item, i) => <li key={i} className="text-sm flex items-start gap-1.5"><CheckCircle className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /><span>{item}</span></li>)}</ul>
              </div>
            )}
            {subject.working_on.length > 0 && (
              <div className="rounded-lg border border-amber-400/60 bg-amber-500/10 p-3 space-y-1.5">
                <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1.5"><TrendingUp className="h-4 w-4" />Getting better</h4>
                <ul className="space-y-1">{subject.working_on.map((item, i) => <li key={i} className="text-sm flex items-start gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" /><span>{item}</span></li>)}</ul>
              </div>
            )}
            {subject.needs_help.length > 0 && (
              <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 space-y-1.5">
                <h4 className="text-sm font-semibold text-rose-800 dark:text-rose-200 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" />Needs more practice</h4>
                <ul className="space-y-1">{subject.needs_help.map((item, i) => <li key={i} className="text-sm flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-rose-600 mt-0.5 shrink-0" /><span>{item}</span></li>)}</ul>
              </div>
            )}
          </div>

          {subject.parent_tips.length > 0 && (
            <div className="rounded-lg border border-primary/25 bg-primary/5 p-3 space-y-1.5">
              <h4 className="text-sm font-semibold flex items-center gap-1.5"><Lightbulb className="h-4 w-4 text-primary" />Tips for parents</h4>
              <ul className="space-y-1">{subject.parent_tips.map((tip, i) => <li key={i} className="text-sm flex items-start gap-1.5 text-muted-foreground"><Lightbulb className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" /><span>{tip}</span></li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OverallSummary({
  studentName,
  report,
  objectivesData,
  localStats,
}: {
  studentName: string;
  report: ReportData | undefined;
  objectivesData: ObjectivesReport | undefined;
  localStats: { totalAttempts: number; correctAttempts: number; overallAccuracy: number } | null;
}) {
  const subjects = deduplicateSubjects(report?.subjects || []);
  const hasLocalData = localStats && localStats.totalAttempts > 0;
  const totalQuestions = hasLocalData ? localStats.totalAttempts : subjects.reduce((s, sub) => s + sub.total_questions, 0);
  const totalCorrect = hasLocalData ? localStats.correctAttempts : subjects.reduce((s, sub) => s + sub.correct, 0);
  const accuracy = hasLocalData ? localStats.overallAccuracy : (totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0);
  const subjectCount = subjects.length;

  const bestSubject = subjects.length > 0
    ? subjects.reduce((best, s) => s.score_percent > best.score_percent ? s : best, subjects[0])
    : null;
  const worstSubject = subjects.length > 0
    ? subjects.reduce((worst, s) => s.score_percent < worst.score_percent ? s : worst, subjects[0])
    : null;

  const masteredCount = objectivesData?.masteredCount || 0;

  if (totalQuestions === 0 && subjectCount === 0) return null;

  const parts: string[] = [];
  if (totalQuestions > 0 && subjectCount > 0) {
    parts.push(`${studentName} has answered ${totalQuestions} questions across ${subjectCount} subjects with an overall accuracy of ${accuracy}%.`);
  } else if (totalQuestions > 0) {
    parts.push(`${studentName} has answered ${totalQuestions} questions with an overall accuracy of ${accuracy}%.`);
  }

  if (bestSubject && worstSubject && bestSubject.name !== worstSubject.name) {
    parts.push(`Their strongest subject is ${bestSubject.name} at ${bestSubject.score_percent}%, while ${worstSubject.name} at ${worstSubject.score_percent}% could use more practice.`);
  } else if (bestSubject) {
    parts.push(`Their strongest subject is ${bestSubject.name} at ${bestSubject.score_percent}%.`);
  }

  if (masteredCount > 0) {
    parts.push(`They've mastered ${masteredCount} curriculum skills so far.`);
  }

  if (parts.length === 0) return null;

  return (
    <FlatSection icon={FileText} sticker="bg-primary" title="In a nutshell" testId="section-summary">
      <p className="text-sm leading-relaxed">{parts.join(" ")}</p>
    </FlatSection>
  );
}

function proficiencyLevel(score: number): string {
  if (score >= 85) return "Highly Proficient";
  if (score >= 70) return "Proficient";
  if (score >= 55) return "Approaching Proficiency";
  if (score >= 40) return "Developing";
  return "Beginning";
}

function proficiencyBadgeVariant(score: number): "default" | "secondary" | "destructive" | "outline" {
  if (score >= 70) return "default";
  if (score >= 55) return "secondary";
  if (score >= 40) return "secondary";
  return "destructive";
}


function masteryLabel(mastery: string): string {
  if (mastery === "mastered") return "Mastered";
  if (mastery === "progressing") return "In Progress";
  return "Not Yet Assessed";
}

function masteryBadgeVariant(mastery: string): "default" | "secondary" | "outline" {
  if (mastery === "mastered") return "default";
  if (mastery === "progressing") return "secondary";
  return "outline";
}

function AdvancedReportView({
  report,
  objectivesData,
  readingStats,
  studentName,
  mode,
}: {
  report: ReportData | undefined;
  objectivesData: ObjectivesReport | undefined;
  reportCardData: any;
  readingStats: ReadingStats | undefined;
  studentName: string;
  mode: "student" | "parent" | "admin";
}) {
  const isStudent = mode === "student" || mode === "admin";
  const subjects = deduplicateSubjects(report?.subjects || []);
  const totalQuestions = subjects.reduce((s, sub) => s + sub.total_questions, 0);
  const totalCorrect = subjects.reduce((s, sub) => s + sub.correct, 0);
  const averageScore = subjects.length > 0 ? Math.round(subjects.reduce((s, sub) => s + sub.score_percent, 0) / subjects.length) : 0;
  const accuracyRate = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
  const reportDate = report?.report_date ? new Date(report.report_date) : new Date();
  const gradeLevel = report?.student?.grade_level || "N/A";

  const proficiencyDistribution: Record<string, number> = {
    "Highly Proficient": 0,
    "Proficient": 0,
    "Approaching Proficiency": 0,
    "Developing": 0,
    "Beginning": 0,
  };
  subjects.forEach((s) => {
    proficiencyDistribution[proficiencyLevel(s.score_percent)]++;
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6 space-y-3">
          <div className="text-center space-y-1">
            <h2 className="text-lg font-bold tracking-tight">Student Performance Assessment Report</h2>
            <p className="text-sm text-muted-foreground">Comprehensive Curriculum-Aligned Analysis</p>
          </div>
          <div className="border-t pt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="text-muted-foreground">Student Name</div>
            <div className="font-semibold">{studentName}</div>
            <div className="text-muted-foreground">Grade Level</div>
            <div className="font-semibold">{gradeLevel}</div>
            <div className="text-muted-foreground">Report Date</div>
            <div className="font-semibold">{format(reportDate, "MMMM d, yyyy")}</div>
            <div className="text-muted-foreground">Assessment Period</div>
            <div className="font-semibold">Current Term</div>
          </div>
          <p className="text-xs text-muted-foreground border-t pt-3">
            This report uses PEP-aligned proficiency descriptors and estimated statistical measures.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Executive Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="text-muted-foreground">Overall Proficiency Level</div>
            <div className="font-semibold flex items-center gap-2 flex-wrap">
              {proficiencyLevel(averageScore)}
              <Badge variant={proficiencyBadgeVariant(averageScore)} className="text-xs">{averageScore}%</Badge>
            </div>
            <div className="text-muted-foreground">Weighted Accuracy Rate</div>
            <div className="font-semibold">{accuracyRate}%</div>
            <div className="text-muted-foreground">Total Assessment Items</div>
            <div className="font-semibold">{totalQuestions}</div>
            <div className="text-muted-foreground">Items Correct</div>
            <div className="font-semibold">{totalCorrect}</div>
            <div className="text-muted-foreground">Subjects Assessed</div>
            <div className="font-semibold">{subjects.length}</div>
            {objectivesData && (
              <>
                <div className="text-muted-foreground">Curriculum Objectives Assessed</div>
                <div className="font-semibold">{objectivesData.totalObjectives}</div>
                <div className="text-muted-foreground">Objectives Mastered</div>
                <div className="font-semibold">{objectivesData.masteredCount}</div>
                <div className="text-muted-foreground">Objectives Progressing</div>
                <div className="font-semibold">{objectivesData.progressingCount}</div>
                <div className="text-muted-foreground">Objectives Not Started</div>
                <div className="font-semibold">{objectivesData.notStartedCount}</div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {subjects.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold">Subject Performance Analysis</h2>
          {subjects.map((subject) => (
            <Card key={subject.name}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="text-sm font-semibold">{subject.name}</CardTitle>
                  <Badge variant={proficiencyBadgeVariant(subject.score_percent)} className="text-xs">
                    {proficiencyLevel(subject.score_percent)}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-xs text-muted-foreground pt-2">
                  <div>Score: <span className="font-semibold text-foreground">{subject.score_percent}%</span></div>
                  <div>Items Attempted: <span className="font-semibold text-foreground">{subject.total_questions}</span></div>
                  <div>Items Correct: <span className="font-semibold text-foreground">{subject.correct}</span></div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                {subject.strands.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold mb-2">Strand-Level Performance</h4>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-1.5 text-muted-foreground font-medium">Strand Name</th>
                          <th className="text-right py-1.5 text-muted-foreground font-medium">Score</th>
                          <th className="text-right py-1.5 text-muted-foreground font-medium">Proficiency Level</th>
                          <th className="text-right py-1.5 text-muted-foreground font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subject.strands.map((strand) => (
                          <tr key={strand.name} className="border-b last:border-b-0">
                            <td className="py-1.5">{strand.name}</td>
                            <td className="text-right py-1.5 font-semibold">{strand.score_percent}%</td>
                            <td className="text-right py-1.5">{proficiencyLevel(strand.score_percent)}</td>
                            <td className="text-right py-1.5">
                              <Badge
                                variant={strand.status === "mastered" ? "default" : strand.status === "progressing" ? "secondary" : "destructive"}
                                className="text-[10px]"
                              >
                                {strand.status === "mastered" ? "Mastered" : strand.status === "progressing" ? "Progressing" : "Needs Support"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="space-y-3">
                  <h4 className="text-xs font-semibold">Competency Classification</h4>
                  {subject.can_do.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Demonstrated Competencies</p>
                      <ul className="space-y-0.5 pl-4">
                        {subject.can_do.map((item, i) => (
                          <li key={i} className="text-xs list-disc">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {subject.working_on.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Emerging Competencies</p>
                      <ul className="space-y-0.5 pl-4">
                        {subject.working_on.map((item, i) => (
                          <li key={i} className="text-xs list-disc">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {subject.needs_help.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Competencies Requiring Intervention</p>
                      <ul className="space-y-0.5 pl-4">
                        {subject.needs_help.map((item, i) => (
                          <li key={i} className="text-xs list-disc">{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {subject.parent_tips.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold mb-1">Recommended Interventions</h4>
                    <ul className="space-y-0.5 pl-4">
                      {subject.parent_tips.map((tip, i) => (
                        <li key={i} className="text-xs list-disc text-muted-foreground">{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isStudent && objectivesData && objectivesData.subjectBreakdown.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold">Curriculum Objectives Mastery Matrix</h2>
          {objectivesData.subjectBreakdown.map((topicGroup) => (
            <Card key={topicGroup.topicName}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">{topicGroup.topicName}</CardTitle>
                <p className="text-xs text-muted-foreground">
                  {topicGroup.masteredCount} of {topicGroup.total} objectives mastered
                </p>
              </CardHeader>
              <CardContent>
                {topicGroup.periods.map((period) => (
                  <div key={period.periodName} className="mb-4 last:mb-0">
                    <p className="text-xs font-medium text-muted-foreground mb-1">{period.periodName}</p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-1.5 text-muted-foreground font-medium">Objective Code</th>
                          <th className="text-left py-1.5 text-muted-foreground font-medium">Description</th>
                          <th className="text-right py-1.5 text-muted-foreground font-medium">Attempts</th>
                          <th className="text-right py-1.5 text-muted-foreground font-medium">Accuracy</th>
                          <th className="text-right py-1.5 text-muted-foreground font-medium">Mastery Status</th>
                          <th className="text-right py-1.5 text-muted-foreground font-medium">Last Assessed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {period.objectives.map((obj) => (
                          <tr
                            key={obj.objectiveCode}
                            className={`border-b last:border-b-0 ${
                              obj.mastery === "mastered"
                                ? "bg-green-50/50 dark:bg-green-950/10"
                                : obj.mastery === "not-started"
                                ? "bg-muted/30"
                                : ""
                            }`}
                          >
                            <td className="py-1.5 font-mono">{obj.objectiveCode}</td>
                            <td className="py-1.5">{obj.objectiveDescription}</td>
                            <td className="text-right py-1.5">{obj.totalAttempts}</td>
                            <td className="text-right py-1.5">{obj.totalAttempts > 0 ? `${obj.accuracy}%` : "-"}</td>
                            <td className="text-right py-1.5">
                              <Badge variant={masteryBadgeVariant(obj.mastery)} className="text-[10px]">
                                {masteryLabel(obj.mastery)}
                              </Badge>
                            </td>
                            <td className="text-right py-1.5 text-muted-foreground">
                              {obj.lastAttemptedAt ? format(new Date(obj.lastAttemptedAt), "MMM d, yyyy") : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {subjects.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Proficiency Distribution Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(proficiencyDistribution).map(([level, count]) => {
              const pct = subjects.length > 0 ? Math.round((count / subjects.length) * 100) : 0;
              return (
                <div key={level} className="space-y-1">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-muted-foreground">{level}</span>
                    <span className="font-semibold">{count} ({pct}%)</span>
                  </div>
                  <Progress value={pct} className="h-1.5" />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card className="bg-muted">
        <CardContent className="p-4 space-y-1.5">
          <p className="text-xs text-muted-foreground">Proficiency levels aligned with Jamaica's National Standards Curriculum (NSC) performance descriptors.</p>
          <p className="text-xs text-muted-foreground">Scores reflect individual student performance on curriculum-aligned questions and are not normed against a population.</p>
          <p className="text-xs text-muted-foreground">Assessment data sourced from adaptive questioning across the EduFocal Academy platform.</p>
          <p className="text-xs text-muted-foreground border-t pt-1.5 mt-1.5">
            Report generated: {format(new Date(), "MMMM d, yyyy 'at' h:mm a")}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ProgressReport({ mode = "student", childId, adminUserId }: { mode?: "student" | "parent" | "admin"; childId?: string; adminUserId?: string }) {
  const { user, isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [refreshing, setRefreshing] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(() => {
    try { return localStorage.getItem("edufocal_advanced_report") === "true"; } catch { return false; }
  });
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (user?.id) {
      try { localStorage.setItem(`quickstart_visited_progress-report_${user.id}`, "true"); } catch {}
    }
  }, [user?.id]);

  const handleBadgeClick = useCallback(() => {
    clickCountRef.current += 1;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    if (clickCountRef.current >= 5) {
      clickCountRef.current = 0;
      const next = !advancedMode;
      setAdvancedMode(next);
      try { localStorage.setItem("edufocal_advanced_report", String(next)); } catch {}
      toast({
        title: next ? "Advanced Assessment Mode activated" : "Standard report view restored",
      });
    } else {
      clickTimerRef.current = setTimeout(() => { clickCountRef.current = 0; }, 2000);
    }
  }, [advancedMode, toast]);

  const isStudent = mode === "student" || mode === "admin";
  const isAdmin = mode === "admin";

  const quizzativeUrl = mode === "parent" && childId
    ? `/api/parent/children/${childId}/quizzative-report`
    : isAdmin && adminUserId
    ? `/api/admin/users/${adminUserId}/quizzative-report`
    : "/api/me/quizzative-report";

  const quizzativeQueryKey = ["quizzative-report", mode, childId || adminUserId];

  const { data: report, isLoading: reportLoading, error: reportError, refetch: refetchReport } = useQuery<ReportData>({
    queryKey: quizzativeQueryKey,
    queryFn: async () => {
      const separator = quizzativeUrl.includes("?") ? "&" : "?";
      const url = `${quizzativeUrl}${separator}_t=${Date.now()}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to load report");
      }
      return res.json();
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const objectivesUrl = isAdmin && adminUserId
    ? `/api/admin/users/${adminUserId}/report-card/objectives`
    : "/api/report-card/objectives";

  const reportCardUrl = isAdmin && adminUserId
    ? `/api/admin/users/${adminUserId}/report-card`
    : "/api/report-card";

  const readingStatsUrl = isAdmin && adminUserId
    ? `/api/admin/users/${adminUserId}/reading/stats`
    : "/api/reading/stats";

  const { data: objectivesData, isLoading: objectivesLoading } = useQuery<ObjectivesReport>({
    queryKey: [objectivesUrl],
    enabled: isStudent && (isAdmin || (isAuthenticated && !!user)),
  });

  const { data: reportCardData, isLoading: reportCardLoading } = useQuery<any>({
    queryKey: [reportCardUrl],
    enabled: isStudent && (isAdmin || (isAuthenticated && !!user)),
  });

  const { data: readingStats } = useQuery<ReadingStats>({
    queryKey: [readingStatsUrl],
    enabled: isStudent && (isAdmin || (isAuthenticated && !!user)),
  });

  const { data: adminUserData } = useQuery<{ firstName?: string; lastName?: string; grade?: number }>({
    queryKey: [`/api/admin/users/${adminUserId}/details`],
    enabled: isAdmin && !!adminUserId,
  });

  const isLoading = reportLoading || (isStudent && (objectivesLoading || reportCardLoading));

  const hasObjectives = objectivesData && objectivesData.totalObjectives > 0;
  const localStats = isStudent ? (() => {
    const objStats = objectivesData?.overallStats;
    const rcStats = reportCardData?.overallStats;
    const totalAttempts = rcStats?.totalAttempts ?? objStats?.totalAttempts ?? 0;
    const correctAttempts = rcStats?.correctAttempts ?? objStats?.correctAttempts ?? 0;
    const overallAccuracy = totalAttempts > 0
      ? Math.round((correctAttempts / totalAttempts) * 100)
      : (rcStats?.overallAccuracy ?? objStats?.overallAccuracy ?? 0);
    return {
      totalAttempts,
      correctAttempts,
      overallAccuracy,
      streak: objStats?.streak ?? 0,
    };
  })() : null;

  const performanceBreakdown = reportCardData?.performanceBreakdown || [];

  // Data moat. Only the child's own view gets these: not the admin lens, not the parent view.
  const isOwnStudentView = mode === "student";
  const ability: AbilityEstimate | null = reportCardData?.ability ?? null;
  const reviewDueTopics: ReviewDueTopic[] = Array.isArray(reportCardData?.reviewDue)
    ? reportCardData.reviewDue
    : [];

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: quizzativeQueryKey });
      if (isStudent) {
        await queryClient.invalidateQueries({ queryKey: [objectivesUrl] });
        await queryClient.invalidateQueries({ queryKey: [reportCardUrl] });
        await queryClient.invalidateQueries({ queryKey: [readingStatsUrl] });
      }
      await refetchReport();
      toast({ title: "Report refreshed", description: "Your latest progress data has been loaded." });
    } catch {
      toast({ title: "Refresh failed", description: "Could not load the report. Please try again.", variant: "destructive" });
    } finally {
      setRefreshing(false);
    }
  };

  const dedupedSubjects = report?.subjects ? deduplicateSubjects(report.subjects) : [];

  const quizzativeOverall = dedupedSubjects.length > 0 ? {
    totalQuestions: dedupedSubjects.reduce((sum, s) => sum + s.total_questions, 0),
    totalCorrect: dedupedSubjects.reduce((sum, s) => sum + s.correct, 0),
    averageScore: Math.round(dedupedSubjects.reduce((sum, s) => sum + s.score_percent, 0) / dedupedSubjects.length),
    subjectCount: dedupedSubjects.length,
  } : null;

  const allStrengths = dedupedSubjects.flatMap(s => s.can_do.map(item => ({ subject: s.name, item })));
  const allWeaknesses = dedupedSubjects.flatMap(s => s.needs_help.map(item => ({ subject: s.name, item })));

  const studentName = report?.student?.name || (isAdmin && adminUserData ? `${adminUserData.firstName || ''} ${adminUserData.lastName || ''}`.trim() : null) || (isAdmin ? "Student" : user?.firstName || "Student");

  return (
    <div className="max-w-4xl mx-auto space-y-4 px-3 py-3 sm:p-4 md:p-6 pb-24 md:pb-8">
      <section className="rounded-2xl bg-primary text-primary-foreground p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6" data-testid="report-hero">
        <div className="flex items-center gap-3 md:contents">
          <Button variant="ghost" size="icon" onClick={() => navigate(isAdmin ? "/admin/xp-decay" : mode === "parent" ? `/parent/child/${childId}` : "/dashboard")} className="shrink-0 text-primary-foreground hover:bg-white/15" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="shrink-0 hidden md:block"><Illustration name="owl-chart" size={104} /></div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Progress Report</h1>
            <Badge
              className={`text-xs font-semibold no-default-hover-elevate no-default-active-elevate cursor-pointer select-none border-0 ${advancedMode ? "bg-emerald-400 text-emerald-950" : "bg-amber-400 text-amber-950"}`}
              onClick={handleBadgeClick}
            >{advancedMode ? "Advanced" : "Beta"}</Badge>
          </div>
          <p className="text-sm md:text-base text-primary-foreground/85 mt-1">
            {isAdmin ? `${studentName}'s` : mode === "parent" ? "Your child's" : "Your"} learning progress and performance · {format(new Date(), "MMMM yyyy")}
          </p>
          {isStudent && localStats && hasObjectives && (
            <div className="mt-3 max-w-md">
              <Bar value={objectivesData.totalObjectives ? Math.round((objectivesData.masteredCount / objectivesData.totalObjectives) * 100) : 0} color="bg-amber-400" track="bg-white/25" h="h-2.5" />
              <p className="text-xs text-primary-foreground/80 mt-1 tabular-nums">{objectivesData.masteredCount} of {objectivesData.totalObjectives} skills mastered</p>
            </div>
          )}
        </div>
        <Button variant="secondary" size="sm" className="rounded-lg bg-white text-primary hover:bg-white/90 shrink-0 self-start md:self-auto" onClick={handleRefresh} disabled={refreshing || isLoading}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing || isLoading ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing..." : "Refresh"}
        </Button>
      </section>

      {isLoading && (
        <div className="rounded-xl border border-border bg-card flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading the report...</p>
          <p className="text-xs text-muted-foreground">This can take a moment while the answers are analysed.</p>
        </div>
      )}

      {!isLoading && advancedMode && (report || localStats || hasObjectives) && (
        <AdvancedReportView
          report={report}
          objectivesData={objectivesData}
          reportCardData={reportCardData}
          readingStats={readingStats}
          studentName={studentName}
          mode={mode}
        />
      )}

      {!isLoading && !advancedMode && reportError && !localStats && !performanceBreakdown.length && (
        <div className="rounded-xl border border-border bg-card p-4 md:p-5">
          <StudentEmptyState illustration="owl-sleepy" title={(reportError as Error).message || "Could not load the report."} hint={`${isAdmin ? "This student" : mode === "parent" ? "Your child" : "You"} may need to answer more quiz questions before a detailed report is available.`} action={<Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>{refreshing ? "Loading..." : "Try again"}</Button>} />
        </div>
      )}

      {!isLoading && !advancedMode && (report || localStats || hasObjectives || performanceBreakdown.length > 0) && (
        <>
          {isStudent && localStats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="report-stats">
              {[
                { icon: Target, value: localStats.totalAttempts.toLocaleString(), label: "Questions tried", tint: "bg-blue-500/10", sticker: "bg-blue-500" },
                { icon: Trophy, value: `${localStats.overallAccuracy}%`, label: "Accuracy", tint: "bg-amber-500/10", sticker: "bg-amber-500" },
                { icon: Award, value: (hasObjectives ? objectivesData.masteredCount : 0).toLocaleString(), label: "Skills mastered", tint: "bg-emerald-500/10", sticker: "bg-emerald-500" },
                { icon: Flame, value: localStats.streak.toLocaleString(), label: "Day streak", tint: "bg-orange-500/10", sticker: "bg-orange-500" },
              ].map((t) => (
                <div key={t.label} className={`rounded-xl border border-border ${t.tint} p-3 md:p-4 flex items-center gap-3`}>
                  <div className={`w-11 h-11 rounded-xl ${t.sticker} text-white flex items-center justify-center shrink-0 -rotate-2 shadow-sm`}><t.icon className="w-5 h-5" /></div>
                  <div className="min-w-0"><div className="text-xl md:text-2xl font-bold tabular-nums leading-tight">{t.value}</div><div className="text-xs font-medium text-muted-foreground">{t.label}</div></div>
                </div>
              ))}
            </div>
          )}

          {!isStudent && quizzativeOverall && (
            <section className="rounded-xl border border-border bg-card p-4 md:p-5 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0"><h2 className="text-base md:text-lg font-semibold truncate">{report?.student?.name}</h2><p className="text-xs text-muted-foreground">{report?.student?.grade_level}</p></div>
                <div className="text-right shrink-0"><div className={`text-3xl font-bold tabular-nums ${scoreColor(quizzativeOverall.averageScore)}`}>{quizzativeOverall.averageScore}%</div><p className="text-xs text-muted-foreground">Overall average</p></div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: Award, value: `${quizzativeOverall.averageScore}%`, label: "Average score", tint: "bg-amber-500/10", sticker: "bg-amber-500" },
                  { icon: BarChart3, value: quizzativeOverall.subjectCount, label: "Subjects tested", tint: "bg-violet-500/10", sticker: "bg-violet-500" },
                  { icon: Target, value: quizzativeOverall.totalQuestions.toLocaleString(), label: "Questions answered", tint: "bg-blue-500/10", sticker: "bg-blue-500" },
                  { icon: CheckCircle, value: quizzativeOverall.totalCorrect.toLocaleString(), label: "Correct answers", tint: "bg-emerald-500/10", sticker: "bg-emerald-500" },
                ].map((t) => (
                  <div key={t.label} className={`rounded-xl border border-border ${t.tint} p-3 flex items-center gap-3`}>
                    <div className={`w-10 h-10 rounded-xl ${t.sticker} text-white flex items-center justify-center shrink-0 -rotate-2 shadow-sm`}><t.icon className="w-5 h-5" /></div>
                    <div className="min-w-0"><div className="text-lg font-bold tabular-nums leading-tight">{t.value}</div><div className="text-xs text-muted-foreground">{t.label}</div></div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <OverallSummary
            studentName={studentName}
            report={report}
            objectivesData={objectivesData}
            localStats={localStats}
          />

          {isOwnStudentView && (
            <MoatCards ability={ability} reviewDue={reviewDueTopics} grade={user?.grade} />
          )}

          {isStudent && hasObjectives && objectivesData.recentWins.length > 0 && (
            <FlatSection icon={Star} sticker="bg-emerald-500" title="Recent wins" hint="Skills mastered lately" tone="border-emerald-500/40 bg-emerald-500/5" testId="section-wins">
              <ul className="space-y-2">
                {objectivesData.recentWins.map((win) => (
                  <li key={win.objectiveCode} className="flex items-start gap-3 p-3 rounded-lg bg-card border border-border">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-snug">{win.objectiveDescription}</p>
                      <p className="text-xs text-muted-foreground">{win.topicName && <>{win.topicName} · </>}Mastered {formatDistanceToNow(new Date(win.masteredAt), { addSuffix: true })}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </FlatSection>
          )}

          {isStudent && hasObjectives && objectivesData.focusAreas.length > 0 && (
            <FlatSection icon={Zap} sticker="bg-amber-500" title="Keep practising" hint="Close on these. A little more and they are yours." tone="border-amber-400/60 bg-amber-500/5" testId="section-focus">
              <ul className="space-y-2">
                {objectivesData.focusAreas.map((focus) => {
                  const practiceUrl = `/learning?objective=${encodeURIComponent(focus.objectiveCode)}&mode=quiz`;
                  const fst = subjectStyleFor(focus.subject || "");
                  return (
                    <li key={focus.objectiveCode} className="p-3 rounded-lg bg-card border border-border space-y-2">
                      <div className="flex items-start gap-3">
                        <TrendingUp className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            {focus.subject && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${fst.chip}`}>{focus.subject}</span>}
                            {focus.attemptTopic && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{focus.attemptTopic}</span>}
                            <ScorePill score={focus.accuracy} />
                          </div>
                          <p className="text-sm font-medium leading-snug">{focus.objectiveDescription}</p>
                          <p className="text-xs text-muted-foreground tabular-nums mt-1">{focus.correctAttempts}/{focus.totalAttempts} correct so far{focus.attemptsNeeded > 0 && <> · about {focus.attemptsNeeded} more correct {focus.totalAttempts < 3 ? "attempts" : "answers"} needed</>}</p>
                        </div>
                        <Button size="sm" className="rounded-lg shrink-0 bg-amber-500 hover:bg-amber-600 text-white" onClick={() => navigate(practiceUrl)}>Practise</Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </FlatSection>
          )}

          {dedupedSubjects.length > 0 && (
            <>
              <div className="space-y-1 pt-2">
                <h2 className="text-base md:text-lg font-semibold flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary flex-shrink-0" />Subjects</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">Tap a subject to see each learning area.</p>
              </div>

              <div className="space-y-3">
                {dedupedSubjects.map((subject) => (
                  <SubjectCard key={subject.name} subject={subject} />
                ))}
              </div>
            </>
          )}

          {(allStrengths.length > 0 || allWeaknesses.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {allStrengths.length > 0 && (
                <FlatSection icon={CheckCircle} sticker="bg-emerald-500" title="Top strengths" tone="border-emerald-500/40 bg-emerald-500/5">
                  <ul className="space-y-2">
                    {allStrengths.slice(0, 6).map((s, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                        <div className="min-w-0"><span className="leading-snug">{s.item}</span> <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${subjectStyleFor(s.subject).chip}`}>{s.subject}</span></div>
                      </li>
                    ))}
                  </ul>
                </FlatSection>
              )}

              {allWeaknesses.length > 0 && (
                <FlatSection icon={AlertTriangle} sticker="bg-rose-500" title="Areas to focus on" tone="border-rose-500/40 bg-rose-500/5">
                  <ul className="space-y-2">
                    {allWeaknesses.slice(0, 6).map((w, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
                        <div className="min-w-0"><span className="leading-snug">{w.item}</span> <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${subjectStyleFor(w.subject).chip}`}>{w.subject}</span></div>
                      </li>
                    ))}
                  </ul>
                </FlatSection>
              )}
            </div>
          )}

          {isStudent && readingStats && readingStats.totalMaterials > 0 && (
            <FlatSection icon={BookOpen} sticker="bg-blue-500" title="Reading" hint={`${readingStats.completed} of ${readingStats.totalMaterials} readers finished · ${Math.floor(readingStats.totalTimeSeconds / 60)} minutes read`} testId="section-reading">
              <Bar value={(readingStats.completed / readingStats.totalMaterials) * 100} color="bg-blue-500" track="bg-blue-500/15" />
              {readingStats.completedMaterials && readingStats.completedMaterials.length > 0 && (
                <ul className="space-y-1.5">
                  {readingStats.completedMaterials.slice(0, 3).map((material) => (
                    <li key={material.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/40">
                      <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <p className="text-sm truncate flex-1">{material.title}</p>
                      <span className="text-xs text-muted-foreground shrink-0">{format(new Date(material.completedAt), "d MMM")}</span>
                    </li>
                  ))}
                </ul>
              )}
            </FlatSection>
          )}

          {isStudent && (!hasObjectives || (hasObjectives && objectivesData.subjectBreakdown.length === 0)) && performanceBreakdown.length > 0 && (
            <FlatSection icon={TrendingUp} sticker="bg-primary" title="Performance by subject" testId="section-performance">
              <div className="space-y-3">
                {Object.entries(performanceBreakdown.reduce((acc: any, item: any) => { (acc[item.subject] ||= []).push(item); return acc; }, {})).map(([subject, topics]: [string, any]) => {
                  const st = subjectStyleFor(subject);
                  return (
                    <div key={subject} className={`rounded-xl border ${st.card} p-3 space-y-2`}>
                      <div className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-sm ${st.bar}`} /><h3 className="text-sm font-semibold capitalize">{subject}</h3></div>
                      {topics.map((topic: any) => (
                        <div key={`${subject}-${topic.topic}`} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-card border border-border flex-wrap">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium capitalize">{topic.topic}</p>
                            <p className="text-xs text-muted-foreground tabular-nums">{topic.correctAnswers}/{topic.totalQuestions} correct</p>
                          </div>
                          <div className="flex items-end gap-3 flex-wrap">
                            <div className="flex flex-col items-start gap-1"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Mastery</span><TopicMasteryBadge mastery={topic.mastery} band={topic.band} testId={`badge-mastery-${topic.topic}`} /></div>
                            <div className="flex flex-col items-start gap-1"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Jamaica</span><TopicNationalStanding percentile={topic.percentile} testId={`text-percentile-${topic.topic}`} /></div>
                            <ScorePill score={topic.accuracy} />
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </FlatSection>
          )}

          <p className="text-xs text-muted-foreground text-center px-4">
            Report generated on {format(report?.report_date ? new Date(report.report_date) : new Date(), "d MMMM yyyy")}. Based on quiz questions answered on EduFocal Academy.
          </p>
        </>
      )}

      {!isLoading && !advancedMode && !report && !localStats && !hasObjectives && !reportError && performanceBreakdown.length === 0 && (
        <div className="rounded-xl border border-border bg-card p-4 md:p-5">
            <StudentEmptyState
              illustration="owl-chart"
              title={isAdmin ? "No data yet" : "Your report starts with one question"}
              hint={isAdmin ? "This student hasn't answered any questions yet." : "Answer a few questions and Owlbert starts charting how you're doing."}
              action={!isAdmin ? <Button className="rounded-lg" onClick={() => navigate("/learning")}>Start learning</Button> : undefined}
            />
        </div>
      )}
    </div>
  );
}
